import React, { ReactNode, useMemo } from "react";
import {
  Box,
  Checkbox,
  CircularProgress,
  DialogContent,
  Divider,
  useTheme,
} from "@mui/material";
import { ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
// import InfoBannerCst from "@components/InfoBannerCst";
import { ButtonPrimary } from "src/UI/ButtonPrimary";
import { ButtonSecondary } from "src/UI/ButtonSecondary";
import { ButtonTertiary } from "src/UI/ButtonTertiary";
import ProgressModalFooter from "../components/ProgressModalFooter";
import ProgressTreeNode from "../components/ProgressTreeNode";
import { ProgressNode } from "../types";
import {
  canEditTracking,
  getCascadeCoveredKeys,
  isNodeCompleted,
  isNodeSelectable,
} from "../utils";

interface SelectElementsViewProps {
  tree: ProgressNode;
  isLoading?: boolean;
  selected: Set<string>;
  onToggle: (node: ProgressNode) => void;
  onCancel: () => void;
  onContinue: () => void;
  canSelect?: boolean;
  showTrackingAction?: boolean;
  onEditTracking?: (node: ProgressNode) => void;
  trackingNode?: ProgressNode | null;
  trackingPanel?: ReactNode;
  canContinue: boolean;
}

const SelectElementsView: React.FC<SelectElementsViewProps> = ({
  tree,
  isLoading,
  selected,
  onToggle,
  onCancel,
  onContinue,
  canSelect = true,
  showTrackingAction,
  onEditTracking,
  trackingNode,
  trackingPanel,
  canContinue,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("enrollment-progress");
  const coveredKeys = useMemo(
    () => getCascadeCoveredKeys(tree, selected),
    [tree, selected],
  );

  const renderCheckbox = (node: ProgressNode) => {
    const completed = isNodeCompleted(node);
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Checkbox
          checked={
            completed || selected.has(node.key) || coveredKeys.has(node.key)
          }
          disabled={!isNodeSelectable(node) || !!trackingNode}
          onChange={() => onToggle(node)}
          sx={{
            color: theme.palette.divider,
            "&.Mui-checked": { color: theme.palette.primary.main },
            padding: "4px",
          }}
        />
        <Divider orientation="vertical" flexItem />
      </Box>
    );
  };

  const renderTrackingAction = (node: ProgressNode) =>
    showTrackingAction && !trackingNode && canEditTracking(node) ? (
      <ButtonTertiary
        size="sm"
        endIcon={<ChevronRight size={14} />}
        disabled={selected.has(node.key) || !onEditTracking}
        onClick={() => onEditTracking?.(node)}
      >
        {t("select.edit-tracking")}
      </ButtonTertiary>
    ) : null;

  // const renderInheritBanner = (node: ProgressNode) =>
  //   node.key === tree.key &&
  //   node.children.length > 0 &&
  //   selected.has(node.key) ? (
  //     <InfoBannerCst>
  //       {t(`select.inherit-banner.${node.entityType}`)}
  //     </InfoBannerCst>
  //   ) : null;

  return (
    <>
      <DialogContent
        sx={{ padding: "12px 24px", mt: 1, display: "flex", gap: 2 }}
      >
        <Box sx={{ flex: 1, minWidth: 0, overflowY: "auto" }}>
          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <ProgressTreeNode
              node={tree}
              renderLeading={canSelect ? renderCheckbox : undefined}
              renderTrailing={renderTrackingAction}
              // renderFooter={renderInheritBanner}
              isHighlighted={(node) =>
                trackingNode
                  ? node.key === trackingNode.key
                  : selected.has(node.key)
              }
              isDimmed={(node) =>
                !!trackingNode && node.key !== trackingNode.key
              }
              isMuted={(node) =>
                canSelect &&
                !trackingNode &&
                !isNodeCompleted(node) &&
                !isNodeSelectable(node)
              }
            />
          )}
        </Box>
        {trackingNode && (
          <Box sx={{ flex: 1, minWidth: 0, overflowY: "auto" }}>
            {trackingPanel}
          </Box>
        )}
      </DialogContent>

      <ProgressModalFooter
        left={
          <ButtonSecondary size="md" onClick={onCancel}>
            {t("actions.cancel")}
          </ButtonSecondary>
        }
        right={
          <ButtonPrimary
            size="md"
            disabled={!canContinue || isLoading}
            onClick={onContinue}
          >
            {t("actions.continue")}
          </ButtonPrimary>
        }
      />
    </>
  );
};

export default SelectElementsView;
